// Product Quick View Functionality with Proper Option Selectors
window.ProductQuickView = {
  // Cache for loaded product data
  cache: {},
  currentProduct: null,
  selectedOptions: {},
  
  // Open quick view for a product
  async open(productHandle, mainProductSelectedSize = null) {
    try {
      // Check if we have a stored size from before cart operations
      const storedSize = window.sessionStorage?.getItem('quickview_last_selected_size');
      if (storedSize && !mainProductSelectedSize) {
        mainProductSelectedSize = storedSize;
      }
      
      // If no size passed, try to detect from current page
      if (!mainProductSelectedSize) {
        // Try multiple approaches to find the selected size
        
        // 1. Check URL for variant parameter
        const urlParams = new URLSearchParams(window.location.search);
        const variantId = urlParams.get('variant');
        
        // 2. Check the current variant ID in the form
        const variantInput = document.querySelector('input[name="id"]');
        const currentVariantId = variantId || variantInput?.value;
        
        // 3. Try to find size from the current URL (size links might update the URL)
        if (currentVariantId) {
          // Look for a link with this variant ID that contains size text
          const variantLink = document.querySelector(`a[href*="variant=${currentVariantId}"]`);
          if (variantLink) {
            const linkText = variantLink.textContent?.trim();
            if (linkText && (linkText.includes('×') || linkText.includes('x'))) {
              mainProductSelectedSize = linkText;
            }
          } else {
            // Variant link might be removed after adding to cart
            // Try to get size from URL parameter
            
            // Check if URL has the variant and try to match with any size links
            const allSizeLinks = Array.from(document.querySelectorAll('a')).filter(a => {
              const text = a.textContent;
              return text && (text.includes('×') || text.match(/\d+["\']?x\d+/i));
            });
            
            // Try to match variant ID with product data if available
            for (const link of allSizeLinks) {
              const linkHref = link.href;
              // Extract variant ID from link
              const variantMatch = linkHref.match(/variant=(\d+)/);
              if (variantMatch && variantMatch[1] === currentVariantId) {
                mainProductSelectedSize = link.textContent.trim();
                break;
              }
            }
          }
        }
        
        // 4. Fallback to DOM detection
        if (!mainProductSelectedSize) {
          // Try to find selected size from various possible selectors
          const sizeSelectors = [
            // Look for any link/button containing size dimensions
            'a:has-text("×"), a:has-text("x")',
            'button:has-text("×"), button:has-text("x")',
            // Common Shopify selectors
            'select[name*="Size"]',
            'input[type="radio"][name*="Size"]:checked',
            'select[name="options[Size]"]',
            'input[name="options[Size]"]:checked',
            // Custom theme selectors
            '.option-button[data-option-name="Size"].border-black',
            '.option-button[data-option-name="Size"].bg-gray-100',
            '[data-option="Size"] .selected',
            'select[data-option-name="Size"]',
            // Any active/selected size button
            '.size-selector .active',
            '.size-option.selected',
            '.selected:has-text("×")',
            '.active:has-text("×")',
            '[aria-selected="true"]:has-text("×")',
            '[aria-pressed="true"]:has-text("×")'
          ];
          
          for (const selector of sizeSelectors) {
            try {
              // For complex selectors with :has-text, we need to handle them specially
              let elements = [];
              if (selector.includes(':has-text')) {
                const [baseSelector, textPart] = selector.split(':has-text');
                const searchText = textPart?.replace(/[()'"]/g, '');
                const baseElements = document.querySelectorAll(baseSelector);
                elements = Array.from(baseElements).filter(el => 
                  el.textContent?.includes(searchText)
                );
              } else {
                const element = document.querySelector(selector);
                if (element) elements = [element];
              }
              
              for (const element of elements) {
                const text = element.value || element.textContent?.trim() || element.dataset?.optionValue;
                // Check if this looks like a size (contains dimensions)
                if (text && (text.includes('×') || text.match(/\d+x\d+/i))) {
                  // Check if this element corresponds to the current variant
                  const href = element.getAttribute('href');
                  if (href && currentVariantId && !href.includes(`variant=${currentVariantId}`)) {
                    continue; // Skip if not the current variant
                  }
                  mainProductSelectedSize = text;
                  break;
                }
              }
              
              if (mainProductSelectedSize) break;
            } catch (e) {
              // Skip invalid selectors
            }
          }
        }
      }
      
      // Store the detected size for future use (in case DOM changes after cart operations)
      if (mainProductSelectedSize && window.sessionStorage) {
        window.sessionStorage.setItem('quickview_last_selected_size', mainProductSelectedSize);
      }
      
      // Check cache first
      let productData = this.cache[productHandle];
      
      if (!productData) {
        // Fetch product data AND swatch images from product page
        // Fetch JSON data
        const response = await fetch(`/products/${productHandle}.js`);
        
        if (!response.ok) {
          throw new Error(`Product fetch failed: ${response.status} ${response.statusText}`);
        }
        
        productData = await response.json();
        
        // Also fetch the product page HTML to extract swatch images
        try {
          const pageResponse = await fetch(`/products/${productHandle}`);
          if (pageResponse.ok) {
            const html = await pageResponse.text();
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');
            
            // Extract swatch images from the page
            const swatchImages = {};
            const swatchElements = doc.querySelectorAll('.color_swatch img');
            
            swatchElements.forEach(img => {
              const label = img.alt || '';
              let src = img.src || '';
              
              // Handle relative URLs
              if (src && !src.startsWith('http')) {
                src = new URL(src, window.location.origin).href;
              }
              
              if (label && src) {
                // Store the swatch image URL, adjust size to 100px for performance
                swatchImages[label] = src.replace(/width=\d+/, 'width=100');
              }
            });
            
            // Add swatch images to product data
            productData.swatchImages = swatchImages;

            // Extract product group swatches HTML from the rendered page
            const pgroupContainer = doc.querySelector('.block_product_group_swatches');
            if (pgroupContainer) {
              productData.pgroupSwatchesHTML = pgroupContainer.outerHTML;
            }
          }
        } catch (err) {
          // Silently fail if we can't fetch swatch images
        }
        
        this.cache[productHandle] = productData;
      }
      
      this.currentProduct = productData;
      
      // Initialize selected options with first available variant's options
      const firstVariant = productData.variants.find(v => v.available) || productData.variants[0];
      this.selectedOptions = {};
      
      // Handle products with single "Title" option differently
      if (productData.options && productData.options.length === 1) {
        const firstOption = productData.options[0];
        const firstOptionName = typeof firstOption === 'string' ? firstOption : firstOption.name;
        if (firstOptionName === 'Title') {
          // Single variant product - no real options to select
          this.selectedOptions = { 'Title': 'Default Title' };
        } else if (firstVariant) {
          // Single option product
          this.selectedOptions[firstOptionName] = firstVariant.options[0];
        }
      } else if (productData.options && firstVariant) {
        // Multi-option product
        productData.options.forEach((option, index) => {
          const optionName = typeof option === 'string' ? option : option.name;
          if (optionName && optionName !== 'Title' && firstVariant.options[index]) {
            this.selectedOptions[optionName] = firstVariant.options[index];
          }
        });
      }
      
      // Check if we should match size from main product
      if (mainProductSelectedSize) {
        // Check if this product has a Size option
        const sizeOption = productData.options.find(opt => {
          const name = typeof opt === 'string' ? opt : opt.name;
          return name && name.toLowerCase() === 'size';
        });
        
        if (sizeOption) {
          const sizeOptionName = typeof sizeOption === 'string' ? sizeOption : sizeOption.name;
          // Get all size values for this product
          const quickViewSizes = this.getOptionValues(productData, sizeOptionName);
          
          // Normalize the size format for comparison
          // Main product might have: 'Medium: 24"x36"' or 'Medium 24×36'
          // Quick view might have: 'Medium: 24x36'
          const normalizeSize = (size) => {
            if (!size) return '';
            // Remove quotes, normalize multiplication signs, remove extra spaces
            return size
              .replace(/["']/g, '')  // Remove quotes
              .replace(/[×✕]/g, 'x')  // Normalize multiplication signs to 'x'
              .replace(/\s*[:]\s*/g, ': ')  // Normalize colon spacing
              .replace(/\s+/g, ' ')  // Normalize spaces
              .trim();
          };
          
          const normalizedMainSize = normalizeSize(mainProductSelectedSize);
          
          // Find matching size in quick view options
          let matchedSize = null;
          for (const qvSize of quickViewSizes) {
            const normalizedQvSize = normalizeSize(qvSize);
            
            // Check for exact match or partial match (dimensions only)
            if (normalizedMainSize === normalizedQvSize) {
              matchedSize = qvSize;
              break;
            }
            
            // Also try matching just the dimensions (e.g., "24x36")
            const mainDimensions = normalizedMainSize.match(/(\d+x\d+)/)?.[1];
            const qvDimensions = normalizedQvSize.match(/(\d+x\d+)/)?.[1];
            if (mainDimensions && qvDimensions && mainDimensions === qvDimensions) {
              matchedSize = qvSize;
              break;
            }
          }
          
          if (matchedSize) {
            this.selectedOptions[sizeOptionName] = matchedSize;
          }
        }
      }
      
      // Build quick view HTML
      const html = this.buildQuickViewHTML(productData);
      
      // Show popup using existing modco.popup function
      if (typeof modco !== 'undefined' && modco.popup) {
        modco.popup(html, 'quickview-popup quickview-modal-custom', 'bg-black bg-opacity-75', 'center');
        
        // Hide the default close button for quickview since we have our own
        setTimeout(() => {
          const defaultCloseBtn = document.querySelector('#modco_popup > button.btn.secondary');
          if (defaultCloseBtn) {
            defaultCloseBtn.style.display = 'none';
          }
        }, 10);
      }
    } catch (error) {
      console.error('Quick view error:', error);
      console.error('Product handle:', productHandle);
      
      // More detailed error message
      let errorMessage = 'Unable to load product details.';
      if (error.message.includes('404')) {
        errorMessage = 'Product not found.';
      } else if (error.message.includes('fetch')) {
        errorMessage = 'Network error. Please check your connection.';
      }
      
      alert(errorMessage + ' Please try again or view the full product page.');
    }
  },
  
  // Build HTML for quick view
  buildQuickViewHTML(product) {
    // Format price
    const formatMoney = (cents) => {
      return '$' + (cents / 100).toFixed(2);
    };
    
    // Get currently selected variant
    const selectedVariant = this.getSelectedVariant(product);
    
    // Build options HTML
    let optionsHTML = '';
    if (product.options && product.options.length > 0 && product.options[0] !== 'Title') {
      optionsHTML = product.options.map((option, optionIndex) => {
        // Handle both string options and option objects
        const optionName = typeof option === 'string' ? option : (option.name || option);
        
        // Skip if option is invalid or is the default "Title" option
        if (!optionName || optionName === 'Title') {
          return '';
        }
        
        const optionValues = this.getOptionValues(product, optionName);
        
        // Check if this is a color/fabric option (for swatches)
        const isColorOption = (optionName && typeof optionName === 'string') ? 
          (optionName.toLowerCase().includes('color') || 
           optionName.toLowerCase().includes('colour') || 
           optionName.toLowerCase().includes('fabric')) : false;
        
        if (isColorOption) {
          // Check if we have any swatch images available
          const hasSwatchImages = optionValues.some(value => {
            const swatchUrl = this.getFabricSwatchUrl(value);
            return swatchUrl && swatchUrl !== '';
          });
          
          // If no swatch images exist, fall back to dropdown
          if (!hasSwatchImages) {
            // Fall through to render as dropdown
          } else {
            // Render color/fabric swatches with images
            return `
              <div class="option-group mb-4" data-option-name="${optionName}">
                <label class="block text-sm font-medium mb-2" style="color: #111827;">${optionName}</label>
                <div class="color-swatches flex gap-2 flex-wrap">
                  ${optionValues.map(value => {
                  const isSelected = this.selectedOptions[optionName] === value;
                  const isAvailable = product.variants.some(v => v.options[optionIndex] === value && v.available);
                  
                  // Get fabric swatch image URL
                  const swatchUrl = this.getFabricSwatchUrl(value);
                  
                  let swatchStyle = '';
                  if (swatchUrl) {
                    // Use the fabric swatch image
                    swatchStyle = `background-image: url('${swatchUrl}'); background-size: cover; background-position: center;`;
                  } else {
                    // Fallback to a textured pattern for fabrics without images (like COM)
                    swatchStyle = `background: linear-gradient(45deg, #e5e5e5 25%, transparent 25%), linear-gradient(-45deg, #e5e5e5 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #e5e5e5 75%), linear-gradient(-45deg, transparent 75%, #e5e5e5 75%); background-size: 8px 8px; background-position: 0 0, 0 4px, 4px -4px, -4px 0px;`;
                  }
                  
                  return `
                    <div class="relative inline-block">
                      <button 
                        type="button"
                        class="color-swatch relative border-2 overflow-hidden transition-all ${isSelected ? 'border-black ring-2 ring-offset-1 ring-black' : 'border-gray-400'} ${!isAvailable ? 'opacity-50 cursor-not-allowed' : 'hover:border-gray-700 hover:scale-110'}"
                        data-option-name="${optionName}"
                        data-option-value="${value}"
                        ${!isAvailable ? 'disabled' : ''}
                        onclick="event.preventDefault(); ProductQuickView.selectOption('${optionName.replace(/'/g, '\\\'')}', '${value.replace(/'/g, '\\\'')}'); return false;"
                        onmouseenter="this.nextElementSibling.style.opacity='1'"
                        onmouseleave="this.nextElementSibling.style.opacity='0'"
                        aria-label="${value}${!isAvailable ? ' - Sold Out' : ''}"
                        style="width: 45px; height: 45px; border-radius: 4px; ${swatchStyle}">
                        ${!isAvailable ? '<span class="absolute inset-0 flex items-center justify-center bg-white bg-opacity-70"><svg class="w-6 h-6" style="color: #2d2a2b;" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg></span>' : ''}
                      </button>
                      <!-- Tooltip - only shows on swatch hover, not tooltip hover -->
                      <div class="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 text-white text-xs font-medium rounded-md whitespace-nowrap z-50 shadow-lg" style="opacity: 0; transition: opacity 0.2s; pointer-events: none; min-width: max-content; background-color: rgba(0, 0, 0, 0.9);">
                        ${value}${!isAvailable ? ' - Sold Out' : ''}
                        <!-- Arrow pointing down -->
                        <div class="absolute top-full left-1/2 transform -translate-x-1/2" style="margin-top: -1px;">
                          <div style="width: 0; height: 0; border-left: 5px solid transparent; border-right: 5px solid transparent; border-top: 5px solid rgba(0, 0, 0, 0.9);"></div>
                        </div>
                      </div>
                    </div>
                  `;
                }).join('')}
              </div>
            </div>
          `;
          }
        }
        
        // Render dropdown for all other options (including Materials, Size, etc.)
          return `
            <div class="option-group mb-4" data-option-name="${optionName}">
              <label class="block text-sm font-medium mb-2" style="color: #111827;">${optionName}</label>
              <select 
                class="w-full p-2 rounded font-medium" 
                style="border: 2px solid #6b7280; color: #2d2a2b; background-color: transparent; outline: none; box-shadow: none;"
                data-option-name="${optionName}"
                onchange="ProductQuickView.selectOption('${optionName}', this.value)">
                ${optionValues.map(value => {
                  const isAvailable = product.variants.some(v => 
                    v.options[optionIndex] === value && 
                    v.available &&
                    this.isOptionComboAvailable(product, optionName, value)
                  );
                  const isSelected = this.selectedOptions[optionName] === value;
                  
                  // Escape quotes in value for HTML attribute
                  const escapedValue = value.replace(/"/g, '&quot;');
                  return `
                    <option 
                      value="${escapedValue}" 
                      ${isSelected ? 'selected' : ''}
                      ${!isAvailable ? 'disabled' : ''}>
                      ${value}${!isAvailable ? ' - Sold Out' : ''}
                    </option>
                  `;
                }).join('')}
              </select>
            </div>
          `;
      }).join('');
    }
    
    // Get main image (prefer selected variant's image if available)
    const mainImage = selectedVariant && selectedVariant.featured_image 
      ? selectedVariant.featured_image.src 
      : product.featured_image;
    
    // Build main HTML structure with improved layout
    const html = `
      <div class="quickview-container" style="position: relative; padding: 1.5rem; width: 90%; max-width: 1280px; margin-left: auto; margin-right: auto; background-color: #d7d3c8; border-radius: 0.5rem;">
        <style>
          @media (min-width: 768px) {
            .quickview-container {
              width: 75% !important;
              padding: 2rem !important;
            }
            .quickview-close-btn {
              top: 0.75rem !important;
              right: 0.75rem !important;
            }
          }
        </style>
        <!-- Close button positioned relative to modal content with inline styles -->
        <button class="quickview-close-btn"
                style="position: absolute; top: 0.5rem; right: 0.5rem; z-index: 10; width: 2rem; height: 2rem; display: flex; align-items: center; justify-content: center; border-radius: 50%; background-color: transparent; border: none; cursor: pointer; transition: background-color 0.2s;" 
                onmouseover="this.style.backgroundColor='#f3f4f6'" 
                onmouseout="this.style.backgroundColor='transparent'"
                onclick="if(typeof modco !== 'undefined' && modco.popup) modco.popup();" 
                aria-label="Close">
          <svg style="width: 1.25rem; height: 1.25rem;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
        <div class="grid md:grid-cols-2 gap-4 md:gap-6">
          <!-- Product Image -->
          <div class="quickview-image">
            <img id="quickview-main-image" 
                 src="${mainImage}" 
                 alt="${product.title}" 
                 class="w-full rounded-lg object-contain aspect-square">
          </div>
          
          <!-- Product Details -->
          <div class="quickview-details">
            <h2 class="text-xl md:text-2xl font-bold mb-3" style="color: #111827;">${product.title}</h2>
            
            <!-- Price -->
            <div class="price-container mb-4">
              <span id="quickview-price" class="text-lg md:text-xl font-semibold" style="color: #2d2a2b;">
                ${selectedVariant ? formatMoney(selectedVariant.price) : formatMoney(product.price)}
              </span>
              ${selectedVariant && selectedVariant.compare_at_price ? `
                <span id="quickview-compare-price" class="line-through ml-2" style="color: #2d2a2b; opacity: 0.7;">
                  ${formatMoney(selectedVariant.compare_at_price)}
                </span>
              ` : ''}
            </div>
            
            <!-- Product Description -->
            ${product.description ? (() => {
              // Strip HTML tags from description
              const tempDiv = document.createElement('div');
              tempDiv.innerHTML = product.description;
              const plainText = tempDiv.textContent || tempDiv.innerText || '';
              
              return `
                <div class="product-description mb-4 text-sm" style="color: #2d2a2b;" id="quickview-description" data-full-text="${product.description.replace(/"/g, '&quot;')}">
                  <span class="description-content">
                    ${window.innerWidth >= 768 ? plainText : (plainText.length > 150 ? plainText.substring(0, 150) + '...' : plainText)}
                  </span>
                  ${plainText.length > 150 && window.innerWidth < 768 ? `
                    <a class="more-link" onclick="ProductQuickView.toggleDescription(true)" style="color: #111827; cursor: pointer; text-decoration: underline; font-weight: 500; margin-left: 0.25rem;">more</a>
                    <a class="less-link" onclick="ProductQuickView.toggleDescription(false)" style="color: #111827; cursor: pointer; text-decoration: underline; font-weight: 500; margin-left: 0.25rem; display: none;">less</a>
                  ` : ''}
                </div>
              `;
            })() : ''}
            
            <!-- View Full Listing Link -->
            <div class="mb-4">
              <a href="/products/${product.handle}" 
                 class="inline-flex items-center text-sm font-medium hover:opacity-70 transition-opacity" 
                 style="color: #2d2a2b; text-decoration: underline;">
                View full listing
                <svg class="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
                </svg>
              </a>
            </div>
            
            <!-- Form -->
            <form id="quickview-form" class="quickview-form">
              <input type="hidden" name="id" id="quickview-variant-id" value="${selectedVariant ? selectedVariant.id : ''}">
              
              <!-- Product Group Swatches -->
              ${product.pgroupSwatchesHTML || ''}

              <!-- Options (Size, Color, etc.) -->
              ${optionsHTML}
              
              <!-- Quantity and Add to Cart responsive layout -->
              <style>
                @media (max-width: 767px) {
                  .quickview-actions {
                    flex-direction: column !important;
                    gap: 0.75rem !important;
                  }
                  .quickview-quantity-wrapper {
                    width: 100% !important;
                    justify-content: center !important;
                  }
                  .quickview-submit-btn {
                    width: 100% !important;
                    padding: 14px 20px !important;
                    min-height: 52px !important;
                  }
                }
              </style>
              <div class="quickview-actions" style="display: flex; gap: 0.75rem; align-items: center;">
                <!-- Quantity Selector -->
                <div class="quickview-quantity-wrapper flex items-center" style="border: 2px solid #6b7280; background: #f3f4f6; min-width: 140px; display: flex;">
                  <button type="button" 
                          onclick="ProductQuickView.updateQuantity(-1)" 
                          class="hover:bg-gray-200 transition-colors"
                          style="color: #111827; width: 46px; height: 48px; display: flex; align-items: center; justify-content: center; border: none; background: transparent; font-size: 20px;">
                    -
                  </button>
                  <input type="number" 
                         name="quantity" 
                         value="1" 
                         min="1" 
                         id="quickview-quantity" 
                         class="text-center font-medium"
                         style="border: none; outline: none; flex: 1; height: 48px; color: #111827; background: transparent; font-size: 16px; padding: 0; -webkit-appearance: textfield; -moz-appearance: textfield; appearance: textfield;">
                  <style>
                    #quickview-quantity::-webkit-inner-spin-button,
                    #quickview-quantity::-webkit-outer-spin-button {
                      -webkit-appearance: none;
                      margin: 0;
                      display: none;
                    }
                    #quickview-quantity {
                      -moz-appearance: textfield;
                    }
                  </style>
                  <button type="button" 
                          onclick="ProductQuickView.updateQuantity(1)" 
                          class="hover:bg-gray-200 transition-colors"
                          style="color: #111827; width: 46px; height: 48px; display: flex; align-items: center; justify-content: center; border: none; background: transparent; font-size: 20px;">
                    +
                  </button>
                </div>
                
                <!-- Add to Cart Button -->
                <button type="submit" 
                        id="quickview-submit"
                        class="quickview-submit-btn text-center font-medium uppercase tracking-wider transition-all ${!selectedVariant || !selectedVariant.available ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-90'}"
                        style="flex: 1; padding: 14px 40px; background-color: #2d2d2d; color: white; border: none; min-height: 52px; font-size: 14px; letter-spacing: 0.1em; font-weight: 500; line-height: 1.5;"
                        ${!selectedVariant || !selectedVariant.available ? 'disabled' : ''}>
                  ${selectedVariant && selectedVariant.available ? 'ADD TO CART' : 'SOLD OUT'}
                </button>
              </div>
            </form>
            
            <!-- Extra padding for better mobile scrolling -->
            <div style="height: 2rem;"></div>
          </div>
        </div>
      </div>
    `;
    
    // Initialize event listeners after popup opens
    setTimeout(() => {
      this.initializeEventListeners();
    }, 100);
    
    return html;
  },
  
  // Get unique option values
  getOptionValues(product, optionName) {
    // First check if the option has values directly (from Shopify option structure)
    const optionObj = product.options.find(opt => {
      const name = typeof opt === 'string' ? opt : opt.name;
      return name === optionName;
    });
    
    if (optionObj && typeof optionObj === 'object' && optionObj.values) {
      // Option has values array directly (modern Shopify structure)
      return optionObj.values;
    }
    
    // Fall back to extracting values from variants
    let optionIndex = -1;
    for (let i = 0; i < product.options.length; i++) {
      const opt = product.options[i];
      const name = typeof opt === 'string' ? opt : opt.name;
      if (name === optionName) {
        optionIndex = i;
        break;
      }
    }
    
    if (optionIndex === -1) {
      return [];
    }
    
    const values = new Set();
    product.variants.forEach(variant => {
      if (variant.options && variant.options[optionIndex]) {
        values.add(variant.options[optionIndex]);
      }
    });
    return Array.from(values);
  },
  
  // Check if option combination is available
  isOptionComboAvailable(product, changedOption, value) {
    const tempOptions = { ...this.selectedOptions, [changedOption]: value };
    const variant = product.variants.find(v => {
      return product.options.every((opt, idx) => {
        const optName = typeof opt === 'string' ? opt : opt.name;
        return v.options[idx] === tempOptions[optName];
      });
    });
    return variant && variant.available;
  },
  
  // Get fabric swatch image URL from scraped data
  getFabricSwatchUrl(fabricName) {
    // First check if we have swatch images from the product page
    const product = this.currentProduct;
    if (product && product.swatchImages && product.swatchImages[fabricName]) {
      // Use the swatch image URL from the product page
      return product.swatchImages[fabricName];
    }
    
    // Special case for COM (Customer's Own Material)
    if (fabricName === "COM") {
      // COM typically shows a placeholder pattern or image
      return null;
    }
    
    // Fallback: try to construct URL based on common patterns
    // This is less reliable but worth trying if we couldn't scrape the images
    const safeName = fabricName
      .replace(/'/g, '')  // Remove apostrophes
      .replace(/\s+/g, '_');  // Replace spaces with underscores
    
    // Try a few common filename patterns
    const patterns = [
      `${safeName}-SMALL.jpg`,
      `${safeName}_SWATCH.jpg`,
      `${safeName.toUpperCase()}_SWATCH.jpg`
    ];
    
    // Return the first pattern as a guess
    return `https://cdn.shopify.com/s/files/1/0609/5971/9651/files/${patterns[0]}?v=1739304334&width=100`;
  },
  
  // Select an option
  selectOption(optionName, value) {
    this.selectedOptions[optionName] = value;
    
    // Note: We no longer have option buttons since everything is either swatches or dropdowns
    
    // Update UI for color swatches
    const colorSwatches = document.querySelectorAll(`.color-swatch[data-option-name="${optionName}"]`);
    colorSwatches.forEach(swatch => {
      const swatchValue = swatch.dataset.optionValue;
      if (swatchValue === value) {
        // This swatch is selected
        swatch.classList.remove('border-gray-400');
        swatch.classList.add('border-black', 'ring-2', 'ring-offset-1', 'ring-black');
      } else {
        // This swatch is not selected
        swatch.classList.remove('border-black', 'ring-2', 'ring-offset-1', 'ring-black');
        swatch.classList.add('border-gray-400');
      }
    });
    
    // Update UI for dropdowns
    const dropdown = document.querySelector(`select[data-option-name="${optionName}"]`);
    if (dropdown) {
      dropdown.value = value;
    }
    
    // Find matching variant
    const variant = this.getSelectedVariant(this.currentProduct);
    
    if (variant) {
      // Update variant ID
      document.getElementById('quickview-variant-id').value = variant.id;
      
      // Update price
      const priceElement = document.getElementById('quickview-price');
      if (priceElement) {
        priceElement.textContent = '$' + (variant.price / 100).toFixed(2);
      }
      
      // Update compare price
      const comparePriceElement = document.getElementById('quickview-compare-price');
      if (comparePriceElement) {
        if (variant.compare_at_price) {
          comparePriceElement.textContent = '$' + (variant.compare_at_price / 100).toFixed(2);
          comparePriceElement.style.display = 'inline';
        } else {
          comparePriceElement.style.display = 'none';
        }
      }
      
      // Update main image if variant has one
      if (variant.featured_image) {
        const mainImage = document.getElementById('quickview-main-image');
        if (mainImage) {
          mainImage.src = variant.featured_image.src;
        }
      }
      
      // Update submit button
      const submitBtn = document.getElementById('quickview-submit');
      if (submitBtn) {
        if (variant.available) {
          submitBtn.textContent = 'ADD TO CART';
          submitBtn.disabled = false;
          submitBtn.classList.remove('opacity-50', 'cursor-not-allowed');
          submitBtn.classList.add('hover:opacity-90');
        } else {
          submitBtn.textContent = 'SOLD OUT';
          submitBtn.disabled = true;
          submitBtn.classList.add('opacity-50', 'cursor-not-allowed');
          submitBtn.classList.remove('hover:opacity-90');
        }
      }
      
      // Update other options availability
      this.updateOptionsAvailability(this.currentProduct);
    }
  },
  
  // Get currently selected variant
  getSelectedVariant(product) {
    return product.variants.find(variant => {
      return product.options.every((option, index) => {
        const optionName = typeof option === 'string' ? option : option.name;
        return variant.options[index] === this.selectedOptions[optionName];
      });
    });
  },
  
  // Update options availability based on current selection
  updateOptionsAvailability(product) {
    product.options.forEach((option, optionIndex) => {
      const optionName = typeof option === 'string' ? option : option.name;
      const isColorOption = optionName.toLowerCase().includes('color') || 
                            optionName.toLowerCase().includes('colour') ||
                            optionName.toLowerCase().includes('fabric');
      
      if (isColorOption) {
        // Only colors/fabrics might have swatches
        // Update color swatches
        document.querySelectorAll(`.color-swatch[data-option-name="${optionName}"]`).forEach(swatch => {
            const value = swatch.dataset.optionValue;
            if (value) {
              const isAvailable = this.isOptionComboAvailable(product, optionName, value);
              swatch.disabled = !isAvailable;
              if (!isAvailable) {
                swatch.classList.add('opacity-50', 'cursor-not-allowed');
              } else {
                swatch.classList.remove('opacity-50', 'cursor-not-allowed');
              }
            }
          });
      } else {
        // Update dropdown options
        const select = document.querySelector(`select[data-option-name="${optionName}"]`);
        if (select) {
          Array.from(select.options).forEach(option => {
            // Extract the base value from the text content if it has " - Sold Out"
            const baseValue = option.value;
            const isAvailable = this.isOptionComboAvailable(product, optionName, baseValue);
            
            // Handle undefined result (shouldn't happen, but safeguard)
            const shouldBeAvailable = isAvailable !== false;
            
            option.disabled = !shouldBeAvailable;
            // Only update text if it needs changing to avoid accumulating suffixes
            const currentText = option.textContent.trim();
            const expectedText = baseValue + (shouldBeAvailable ? '' : ' - Sold Out');
            if (currentText !== expectedText) {
              option.textContent = expectedText;
            }
          });
        }
      }
    });
  },
  
  // Initialize event listeners for the quick view form
  initializeEventListeners() {
    // Form submit
    const form = document.getElementById('quickview-form');
    if (form) {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        
        const formData = new FormData(form);
        const submitBtn = form.querySelector('button[type="submit"]');
        
        // Disable button and show loading state
        const originalText = submitBtn.textContent;
        submitBtn.textContent = 'Adding...';
        submitBtn.disabled = true;
        
        try {
          // Get cart items component section IDs like the product form does
          const cartItemsComponents = document.querySelectorAll('cart-items-component');
          let cartItemComponentsSectionIds = [];
          cartItemsComponents.forEach((item) => {
            if (item.dataset.sectionId) {
              cartItemComponentsSectionIds.push(item.dataset.sectionId);
            }
          });
          
          // Add sections to the form data to get updated HTML
          if (cartItemComponentsSectionIds.length > 0) {
            formData.append('sections', cartItemComponentsSectionIds.join(','));
          }
          
          // Also add sections_url for proper rendering
          formData.append('sections_url', window.location.pathname);
          
          // Add to cart with sections (matching product-form.js approach exactly)
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            body: formData,
            headers: {
              'X-Requested-With': 'XMLHttpRequest',
              'Accept': 'text/html'
            }
          });
          
          if (response.ok) {
            const responseData = await response.json();
            
            
            // Call the theme's cart update function to trigger proper cart refresh
            // The theme uses modco.get_cart() to fetch the cart and update UI
            if (typeof modco !== 'undefined' && modco.get_cart) {
              modco.get_cart();
            } else {
              // Fallback: fetch cart manually and trigger the theme's cart conditions
              fetch('/cart.js')
                .then(response => response.json())
                .then(cart => {
                  
                  // Try to call the theme's cart conditions function
                  if (typeof modco !== 'undefined' && modco.cart_conditions) {
                    modco.cart_conditions(cart);
                  }
                  
                  // Also dispatch a custom event for any other listeners
                  const cartEvent = new CustomEvent('cart:updated', { 
                    detail: { 
                      cart: cart,
                      addedItem: responseData
                    },
                    bubbles: true
                  });
                  document.dispatchEvent(cartEvent);
                })
                .catch(error => {
                  // Silently handle error
                });
            }
            
            // Close popup
            if (typeof modco !== 'undefined' && modco.popup) {
              modco.popup();
            }
            
            // Show success message
            const successMsg = document.createElement('div');
            successMsg.className = 'fixed top-4 right-4 bg-green-500 text-white px-4 py-2 rounded shadow-lg z-50';
            successMsg.textContent = 'Added to cart!';
            document.body.appendChild(successMsg);
            setTimeout(() => successMsg.remove(), 3000);
            
          } else {
            throw new Error('Failed to add to cart');
          }
        } catch (error) {
          alert('Failed to add item to cart. Please try again.');
          
          // Restore button state
          submitBtn.textContent = originalText;
          submitBtn.disabled = false;
        }
      });
    }
  },
  
  // Update quantity
  updateQuantity(change) {
    const input = document.getElementById('quickview-quantity');
    if (input) {
      const newValue = Math.max(1, parseInt(input.value) + change);
      input.value = newValue;
    }
  },

  // Toggle description expansion
  toggleDescription(expand) {
    const container = document.getElementById('quickview-description');
    if (!container) return;
    
    const content = container.querySelector('.description-content');
    const moreLink = container.querySelector('.more-link');
    const lessLink = container.querySelector('.less-link');
    const fullText = container.dataset.fullText;
    
    if (expand && fullText) {
      // Strip HTML tags and decode HTML entities
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = fullText;
      const plainText = tempDiv.textContent || tempDiv.innerText || '';
      content.textContent = plainText;
      if (moreLink) moreLink.style.display = 'none';
      if (lessLink) lessLink.style.display = 'inline';
    } else if (!expand && fullText) {
      // Strip HTML tags for truncated version too
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = fullText;
      const plainText = tempDiv.textContent || tempDiv.innerText || '';
      content.textContent = plainText.substring(0, 150) + '...';
      if (moreLink) moreLink.style.display = 'inline';
      if (lessLink) lessLink.style.display = 'none';
    }
  }
};

// Make it globally accessible
window.ProductQuickView = ProductQuickView;

// Watch for size changes on the main product page
(function() {
  // Store size whenever a size link is clicked
  document.addEventListener('click', function(e) {
    const target = e.target.closest('a');
    if (target && target.href && target.href.includes('variant=')) {
      const text = target.textContent?.trim();
      // Check if this looks like a size option
      if (text && (text.includes('×') || text.match(/\d+["\']?x\d+/i))) {
        window.sessionStorage?.setItem('quickview_last_selected_size', text);
      }
    }
  });
  
  // Also watch for form changes (variant input changes)
  const observer = new MutationObserver(function(mutations) {
    mutations.forEach(function(mutation) {
      if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
        const input = mutation.target;
        if (input.name === 'id' && input.value) {
          // Variant changed, try to find corresponding size
          const variantLink = document.querySelector(`a[href*="variant=${input.value}"]`);
          if (variantLink) {
            const text = variantLink.textContent?.trim();
            if (text && (text.includes('×') || text.match(/\d+["\']?x\d+/i))) {
              window.sessionStorage?.setItem('quickview_last_selected_size', text);
            }
          }
        }
      }
    });
  });
  
  // Start observing variant inputs
  const variantInputs = document.querySelectorAll('input[name="id"]');
  variantInputs.forEach(input => {
    observer.observe(input, { attributes: true, attributeFilter: ['value'] });
  });
})();